import {
	BiLogoFacebookCircle,
	BiLogoInstagram,
	BiLogoLinkedinSquare,
	BiLogoYoutube,
} from 'react-icons/bi'
import { FaXTwitter } from 'react-icons/fa6'
import logoFile from '@/assets/logo.png'
import { useRootLoaderData } from '@/root'
import { DataBackground, UiLink, UiNavbar, UiNavLink } from '@/types/components'
import Background from '@/components/ui/background'
import { RootLoaderData } from '@/libs/strapi/rootLoader'
import { ApiEntityEntity } from '@/types/contentTypes'
import { UiFooter } from '@/types/components'
import Image from '@/components/ui/image'
import Row from '@/components/layout/row'

type ImageProps = {
	url?: string
	src: string
	alt?: string
}

type Links = {
	title: string
	url: string
}

type SocialMediaLinks = {
	url: string
	icon: React.ReactNode
}

type ColumnLinks = {
	links: Links[]
}

type Address = {
	label: string
	value: string
}

type Contact = {
	label: string
	phone: string
	email: string
}

type Props = {
	logo: ImageProps
	address: Address
	contact: Contact
	columnLinks: ColumnLinks[]
	socialMediaLinks: SocialMediaLinks[]
	footerText?: string
	footerLinks: Links[]
}

export type Footer3Props = React.ComponentPropsWithoutRef<'section'> &
	Partial<Props>

export const Footer = (props: Footer3Props) => {
	const { socialMediaLinks, footerText, footerLinks } = {
		...Footer3Defaults,
		...props,
	} as Props

	const rootLoader: RootLoaderData = useRootLoaderData() as RootLoaderData

	const navigation: UiNavbar['attributes'] = rootLoader.navigation
	const footer: UiFooter['attributes'] = rootLoader.footer
	const background: DataBackground['attributes'] = footer.background
	const brand: ApiEntityEntity['attributes'] = rootLoader.brand


	const backgroundColor = background.background_color

	const logo =
		backgroundColor === 'primary' ||
		backgroundColor === 'secondary' ||
		backgroundColor === 'accent' ||
		backgroundColor === 'background' ? (
			<Image image={brand.logo_square_on_dark} />
		) : (
			<Image image={brand.logo_square_on_light} />
		)


	return (
		<Row
			rowSettings={{
				full_width: true,
				after_row_spacing: 'none',
				vertical_spacing: 'none',
				title: 'Footer',
				background: background,
			}}
		>
			<footer className="px-[5%] py-12 md:py-18 lg:py-20">
				<div className="container">
					<div className="grid grid-cols-1 gap-x-[4vw] gap-y-12 pb-12 md:gap-y-16 md:pb-18 lg:grid-cols-[1fr_0.5fr] lg:gap-y-4 lg:pb-20">
						<div>
							<div className="rb-6 mb-6 h-36 w-36 md:mb-8">{logo}</div>
							<div className="rb-6 mb-6 md:mb-8">
								<div>
									<p className="mb-1 text-sm font-semibold">
										{brand.contact.address.address_line_1}{' '}
										{brand.contact.address.address_line_2}
									</p>
									<p className="mb-5 text-sm md:mb-6">
										{brand.contact.address.city}, {brand.contact.address.state}{' '}
									</p>
								</div>
								<div>
									<p className="contast-decoration flex flex-col text-sm underline underline-offset-1 md:mb-6">
										<a href={`tel:${brand.contact.phone}`}>
											{brand.contact.phone}
										</a>
										<a href={`mailto:${brand.contact.email}`}>
											{brand.contact.email}
										</a>
									</p>
								</div>
							</div>
							<div className="grid grid-flow-col grid-cols-[max-content] items-start justify-start gap-x-3 gap-y-0">
								{socialMediaLinks.map((link, index) => (
									<a key={index} href={link.url}>
										{link.icon}
									</a>
								))}
							</div>
						</div>
						<div className="grid grid-cols-1 items-start gap-x-6 gap-y-10 md:grid-cols-2 md:gap-x-8 md:gap-y-4">
							<ul>
								{footer.nav_list_1.map(
									(link: UiNavLink['attributes'], index) => (
										<li key={index} className="py-2 text-sm font-semibold">
											<a href={link.page.seo.slug}>{link.title}</a>
										</li>
									),
								)}
							</ul>
							<ul>
								{footer.nav_list_2.map(
									(link: UiNavLink['attributes'], index) => (
										<li key={index} className="py-2 text-sm font-semibold">
											<a href={link.page.seo.slug}>{link.title}</a>
										</li>
									),
								)}
							</ul>
						</div>
					</div>
					<div className="contrast-bg h-px w-full" />
					<div className="flex flex-col-reverse items-start justify-between pb-4 pt-6 text-sm md:flex-row md:items-center md:pb-0 md:pt-8">
						<p className="mt-8 md:mt-0">{footerText}</p>
						<ul className="grid grid-flow-row grid-cols-[max-content] justify-center gap-x-0 gap-y-4 text-sm md:grid-flow-col md:gap-x-6 md:gap-y-0">
							{footerLinks.map((link, index) => (
								<li key={index} className="contrast-text underline">
									<a href={link.url}>{link.title}</a>
								</li>
							))}
						</ul>
					</div>
				</div>
			</footer>
		</Row>
	)
}

export const Footer3Defaults: Footer3Props = {
	logo: {
		url: '#',
		src: logoFile,
		alt: 'Logo image',
	},
	address: {
		label: 'Address:',
		value: '123 Address Lane Naples FL',
	},
	contact: {
		label: 'Contact:',
		phone: '1800 123 4567',
		email: 'info@lakeparkdiner.com',
	},
	columnLinks: [
		{
			links: [
				{ title: 'Our Story', url: '#' },
				{ title: 'Our Brands', url: '#' },
				{ title: 'Press Kit', url: '#' },
				{ title: 'Events', url: '#' },
				{ title: 'News', url: '#' },
			],
		},
		{
			links: [
				{ title: 'Careers', url: '#' },
				{ title: 'Contact', url: '#' },
				{ title: 'Locations', url: '#' },
				{ title: 'Link Nine', url: '#' },
				{ title: 'Link Ten', url: '#' },
			],
		},
	],
	socialMediaLinks: [
		{ url: '#', icon: <BiLogoFacebookCircle className="size-6" /> },
		{ url: '#', icon: <BiLogoInstagram className="size-6" /> },
		{ url: '#', icon: <FaXTwitter className="size-6 p-0.5" /> },
		{ url: '#', icon: <BiLogoLinkedinSquare className="size-6" /> },
		{ url: '#', icon: <BiLogoYoutube className="size-6" /> },
	],
	footerText: '© 2024 Relume. All rights reserved.',
	footerLinks: [
		{ title: 'Privacy Policy', url: '#' },
		{ title: 'Terms of Service', url: '#' },
		{ title: 'Cookies Settings', url: '#' },
	],
}
